import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-fc054262"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "main-wrapper" }

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import CmTable from '@/components/cmTable/index.vue'
import type { TableOpt } from '@/components/cmTable/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import CmTableSelectionTipBar from '@/views/dashboard/_components/cmtable-selection-tip-bar/cmtable-selection-tip-bar.vue'
import UnauthorizedTooltip from '@/views/dashboard/_components/unauthorized-tooltip/unauthorized-tooltip.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import { TableDataRowState, TableState } from '@/services/table-memory-pagination'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { formatTimeStamp } from '@/utils/time'
import { listCol } from '@/utils/list-columns-i18n'
import {
  TerminalDrawerService,
  QueryParams,
  TerminalItem,
  ScanParams
} from './terminal-drawer.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'terminal-drawer',
  emits: ["syncScanTskId", "closeTerminalDrawer", "authSuccess"],
  setup(__props, { emit: __emit }) {

const emit = __emit

const { t, locale } = useI18n()
const injector = useReflectiveInjector([TerminalDrawerService])
const service = injector.get(TerminalDrawerService)

const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth,
}

function isDisabled(row: TerminalItem) {
  return getAuthByList(row.auth_status, ModulePermissionKey[row.area_type]) !== TerminalAuthStatus.normal
}

service.isDisabled = isDisabled

const tableRef = ref<{ getList: () => Promise<unknown> } | null>(null)
const { refreshList } = useCmTableListRefresh(tableRef)

const listForm = reactive<QueryParams>({
  group_id: -1,
  key_word: '',
  page: 1,
  limit: 20,
  asc: false,
  sort: '',
  useMemory: false,
  // eslint-disable-next-line no-magic-numbers
  system_types: [1, 2, 3],
  permission_names: [ModulePermissionKey[AreaType.linux]],
})

const viewData = ref({
  tableData: [] as TableDataRowState<TerminalItem>[],
  state: {} as TableState<TableDataRowState<TerminalItem>>,
  total: 0,
})

/** CmTable dataFormate 保持行引用（不可与 { ...row } 新对象并用 TableMemoryPagination），仅同步「全选本组」下的 $selected */
function listDataFormate(rows: TableDataRowState<TerminalItem>[] = []) {
  const selectAll = service.getViewState().selectAll
  for (const row of rows) {
    if (selectAll && !isDisabled(row)) {
      row.$selected = true
    }
  }
  return rows
}

const leakScanStatusI18n = computed(() => {
  void locale.value
  return [
    t('safeProtectionModule.leakScanStatus0'),
    t('safeProtectionModule.leakScanStatus1'),
    t('safeProtectionModule.leakScanStatus2'),
    t('safeProtectionModule.leakScanStatus3'),
    t('safeProtectionModule.leakScanStatus4'),
  ]
})

function pullServiceState() {
  const state = service.getViewState()
  viewData.value.state = state
  viewData.value.tableData = state.data.list
  viewData.value.total = state.data.total
}

async function listApi(params: Record<string, unknown>) {
  listForm.page = Number(params.page) || 1
  listForm.limit = Number(params.limit) || 20
  if (params.sort !== undefined) {
    listForm.sort = params.sort as string
  }
  if (params.asc !== undefined) {
    listForm.asc = params.asc as boolean
  }
  await service.refresh(listForm)
  pullServiceState()
  return {
    list: viewData.value.tableData,
    total: viewData.value.total
  }
}

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    showTip: false,
    selection: false,
    rowKey: 'device_id',
    path: 'list',
    totalPath: 'total',
    height: '100%',
    usePagination: false,
    disableScrollLoad: false,
    showActionBar: false,
    showSearch: false,
    showRightToolbar: false,
    bodyStyle: 'height: 100%; padding: 10px 20px 0',
    containerOpt: { style: { flex: '1', minHeight: 0, overflow: 'hidden' } },
    columns: [
      {
        prop: '__sel',
        label: '',
        width: 52,
        columnOpt: { fixed: 'left', align: 'center' }
      },
      {
        prop: 'computer_name',
        label: listCol(t, 'computerName'),
        type: 'COMPUTER',
        columnOpt: { minWidth: 180 }
      },
      {
        prop: 'user_mark',
        label: listCol(t, 'userMark'),
        columnOpt: { minWidth: 130 },
        formatter: (_row: TerminalItem, value: string) => value || '--'
      },
      {
        prop: 'system_ip',
        label: listCol(t, 'ipAddress'),
        type: 'IP',
        columnOpt: { minWidth: 150 }
      },
      {
        prop: 'system_name',
        label: t('safeProtectionModule.os'),
        columnOpt: { minWidth: 140 }
      },
      {
        prop: 'scan_status',
        label: t('safeProtectionModule.scanStatusCol'),
        columnOpt: { minWidth: 130 }
      },
      {
        prop: 'last_scan_time',
        label: t('safeProtectionModule.lastScanCol'),
        columnOpt: { minWidth: 160 },
        formatter: (_row: TerminalItem, value: number) => {
          return value ? formatTimeStamp(value, 'YYYY-MM-DD HH:mm') : t('safeProtectionModule.notScanned')
        }
      },
    ]
  }
})

const filterFormColumns = computed<Partial<ObjectFormItem<QueryParams, keyof QueryParams>>>(() => {
  void locale.value
  return {
    group_id: {
      colOpt: { style: 'flex:0 0 150px' },
      slot: true,
      label: t('safeProtectionModule.group'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => getData(),
      },
    },
    key_word: {
      colOpt: { style: 'flex: 0 0 260px' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Input,
      placeholder: t('common.phComputerUserIp'),
      modelModifiers: { trim: true },
      inputOpt: {
        'suffix-icon': 'el-icon-search',
      },
      events: {
        blur: () => getData(),
        keypress: (e: any) => e.charCode === 13 && getData(),
      },
    },
  }
})

function rowSelectable(row: TableDataRowState<TerminalItem>) {
  return !isDisabled(row)
}

const hasSelectableRows = computed(() => viewData.value.tableData.some(rowSelectable))

function pageSelectAllChecked(rows: TableDataRowState<TerminalItem>[]) {
  const enabled = rows.filter((r) => rowSelectable(r))
  return enabled.length > 0 && enabled.every((r) => r.$selected)
}

function pageSelectAllIndeterminate(rows: TableDataRowState<TerminalItem>[]) {
  const enabled = rows.filter((r) => rowSelectable(r))
  return enabled.some((r) => r.$selected) && !enabled.every((r) => r.$selected)
}

function pageHeaderAllDisabled(rows: TableDataRowState<TerminalItem>[]) {
  if (!rows.length) return true
  return !rows.some((r) => rowSelectable(r))
}

function handlePageSelectAllHeader(rows: TableDataRowState<TerminalItem>[], checked: boolean) {
  const enabled = rows.filter((r) => rowSelectable(r))
  if (checked) {
    service.mergeSelectedData(enabled)
    pullServiceState()
    return
  }
  service.mergeSelectedData([])
  pullServiceState()
}

function handleCheckbox(row: TableDataRowState<TerminalItem>) {
  if (viewData.value.state.selectAll) return
  service.mergeSelectedData(service.getSelectedData(), row)
  pullServiceState()
}

async function getData(isKeepPage = false) {
  if (!isKeepPage) {
    listForm.page = 1
  }
  await refreshList()
}

function handleSearch(value: string) {
  listForm.key_word = value
  getData()
}

function handleQuery(form: Record<string, unknown>) {
  Object.assign(listForm, form)
  getData()
}

function handleReset() {
  listForm.group_id = -1
  listForm.key_word = ''
  getData()
}

function updateGroupId(groupId: number) {
  if (listForm.group_id !== groupId) {
    listForm.useMemory = false
    service.cancelSelectAll()
    pullServiceState()
    listForm.group_id = groupId
    getData()
  }
}

/** 切换「仅查看已选」内存数据模式 */
function getListByMemory() {
  listForm.useMemory = !listForm.useMemory
  getData()
}

async function scanTer() {
  const params: ScanParams = { select_type: 0, device_list: [], group_id: listForm.group_id }
  if (viewData.value.state.selectAll) {
    params.select_type = 1
  } else {
    params.device_list = service.getSelectedData().map(item => item.device_id)
  }
  try {
    const res = await service.getLeakScan(params)
    localStorage.setItem('VULNERABILITY_SCAN_TSK_ID', res.task_id)
    emit('syncScanTskId', res.task_id)
    emit('closeTerminalDrawer')
  } catch {
    ElMessage.error(t('common.failed'))
  }
}

async function selectAll() {
  listForm.page = 1
  listForm.useMemory = false
  service.selectAll()
  pullServiceState()
  // 刷新列表让 CmTable 重新渲染当前页行，checkbox 才能同步为勾选状态
  await getData()
}

async function selectCancel() {
  listForm.useMemory = false
  service.cancelSelectAll()
  pullServiceState()
  await getData()
}

function calcSelectAllScopeDisabled() {
  if (!viewData.value.total || viewData.value.state.selectAll) return true
  return !hasSelectableRows.value
}

async function handleAuthSuccess() {
  await getData()
  emit('authSuccess')
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: listForm,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((listForm) = $event)),
      class: "terminal-drawer-cmtable",
      "table-opt": tableOpt.value,
      api: listApi,
      "data-formate": listDataFormate,
      "page-option": { pageNumName: 'page', pageSizeName: 'limit' },
      "first-loading": true
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"0","background-color":"transparent"},
          modelValue: listForm,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((listForm) = $event)),
          "form-columns": filterFormColumns.value,
          "show-search": true,
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          group_id: _withCtx(() => [
            _createVNode(GroupTreeSelector, {
              width: "100%",
              "terminal-type": [1, 2, 3],
              "model-value": listForm.group_id,
              onSelected: updateGroupId
            }, null, 8, ["model-value"])
          ]),
          actions: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              disabled: !viewData.value.state.selectCount,
              size: "small",
              onClick: scanTer
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('safeProtectionModule.startScan')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns"]),
        _createVNode(CmTableSelectionTipBar, {
          "bar-height": 32,
          class: "cm-ui-mt10"
        }, {
          summary: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('cmtable.selectedPrefix')), 1),
            _createElementVNode("a", {
              class: _normalizeClass(["selection-count-link", !listForm.useMemory ? 'req-primary' : 'req-default']),
              onClick: getListByMemory
            }, _toDisplayString(viewData.value.state.selectCount), 3),
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('safeProtectionModule.terminalUnit')), 1)
          ]),
          actions: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "text",
              class: "cm-btn-text",
              disabled: calcSelectAllScopeDisabled(),
              onClick: selectAll
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('cmtable.selectAllInGroup')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_component_el_button, {
              type: "text",
              class: "cm-btn-text",
              disabled: !viewData.value.state.selectCount,
              onClick: selectCancel
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('cmtable.clearSelection')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      "__sel-header": _withCtx(() => [
        _createVNode(_component_el_checkbox, {
          "model-value": pageSelectAllChecked(viewData.value.tableData),
          disabled: !viewData.value.tableData.length || viewData.value.state.selectAll || pageHeaderAllDisabled(viewData.value.tableData),
          indeterminate: pageSelectAllIndeterminate(viewData.value.tableData),
          onChange: _cache[1] || (_cache[1] = (v) => handlePageSelectAllHeader(viewData.value.tableData, !!v))
        }, null, 8, ["model-value", "disabled", "indeterminate"])
      ]),
      __sel: _withCtx(({ row }) => [
        _createVNode(UnauthorizedTooltip, {
          "permission-key": ModulePermissionKey[row.area_type],
          "device-id": row.device_id,
          "auth-status": _unref(getAuthByList)(row.auth_status, ModulePermissionKey[row.area_type]),
          "area-type": row.area_type,
          onAuthSuccess: handleAuthSuccess
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_checkbox, {
              "model-value": row.$selected,
              disabled: viewData.value.state.selectAll || isDisabled(row),
              onChange: () => handleCheckbox(row)
            }, null, 8, ["model-value", "disabled", "onChange"])
          ]),
          _: 2
        }, 1032, ["permission-key", "device-id", "auth-status", "area-type"])
      ]),
      scan_status: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(leakScanStatusI18n.value[row.scan_status]) + " ", 1),
        _createElementVNode("div", {
          class: _normalizeClass({ 'dot-flashing': row.scan_status === 1 })
        }, null, 2)
      ]),
      _: 1
    }, 8, ["modelValue", "table-opt"])
  ]))
}
}

})